import { useState } from 'react';
import { Mentor } from '../../../mocks/mentors';
import PaymentRequestModal from './PaymentRequestModal';

interface Props {
  mentor: Mentor;
  hasPurchased?: boolean;
  onPurchase?: (payload: { bookingNo: string; orderStatus: number }) => void;
  onGoToBooking?: () => void;
}

const STANDARD_INCLUDES = [
  { icon: 'ri-video-chat-line', text: '高清視訊通話（Zoom / Google Meet）' },
  { icon: 'ri-file-text-line', text: '諮詢錄音 & 會議紀要整理傳送' },
  { icon: 'ri-mail-line', text: '7 天後續電子郵件答疑支援' },
];

const ServicePackagesCard = ({ mentor, hasPurchased = false, onPurchase, onGoToBooking }: Props) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-br from-[#0A1628] to-[#1e3a5f] px-5 py-5">
          <div className="flex items-center gap-3 mb-4">
            <img
              src={mentor.avatar}
              alt={mentor.name}
              className="w-11 h-11 rounded-full object-cover object-top ring-2 ring-white/25 flex-shrink-0"
            />
            <div>
              <p className="text-white font-bold text-sm leading-tight">{mentor.name}</p>
              <p className="text-white/50 text-xs mt-0.5">{mentor.company}</p>
            </div>
          </div>

          <div className="flex items-end gap-2 mb-1">
            <span className="text-4xl font-black text-white">${mentor.price}</span>
            <span className="text-white/50 text-sm mb-1">/ {mentor.consultDuration}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-violet-300 bg-violet-500/20 border border-violet-400/30 px-2.5 py-1 rounded-full">
              1V1 專屬視訊諮詢
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="px-5 py-5">
          {/* Mentor-specific highlights */}
          <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-3">本次諮詢專項內容</p>
          <div className="flex flex-col gap-2.5 mb-5">
            {mentor.consultHighlights.map((h) => (
              <div key={h} className="flex items-start gap-2.5">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                  <i className="ri-checkbox-circle-fill text-violet-500 text-base" />
                </div>
                <span className="text-sm text-slate-700 leading-snug">{h}</span>
              </div>
            ))}
          </div>

          {/* Divider */}
          <div className="border-t border-slate-100 mb-4" />

          {/* Standard inclusions */}
          <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-3">所有諮詢標配</p>
          <div className="flex flex-col gap-2 mb-6">
            {STANDARD_INCLUDES.map((item) => (
              <div key={item.text} className="flex items-center gap-2.5">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-emerald-500 text-sm`} />
                </div>
                <span className="text-xs text-slate-500">{item.text}</span>
              </div>
            ))}
          </div>

          {/* CTA */}
          {hasPurchased ? (
            <button
              onClick={onGoToBooking}
              className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-xl cursor-pointer transition-all active:scale-[0.98] whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-calendar-check-line text-base" />
              已有有效預約，去選擇時間
            </button>
          ) : (
            <button
              onClick={() => setShowModal(true)}
              className="w-full py-3.5 bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold rounded-xl cursor-pointer transition-all active:scale-[0.98] whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-calendar-check-line text-base" />
              立即預約 · ${mentor.price}
            </button>
          )}

          <div className="mt-3 flex items-center justify-center gap-2 text-xs text-slate-400">
            <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse flex-shrink-0" />
            接受預約中 · 通常 24h 內確認
          </div>
        </div>
      </div>

      {showModal && !hasPurchased ? (
        <PaymentRequestModal
          mentorId={mentor.id}
          mentorName={mentor.name}
          mentorAvatar={mentor.avatar}
          mentorCompany={mentor.company}
          consultPrice={mentor.price}
          consultDuration={mentor.consultDuration}
          onClose={() => setShowModal(false)}
          onSuccess={(payload) => {
            setShowModal(false);
            onPurchase?.(payload);
          }}
        />
      ) : null}
    </>
  );
};

export default ServicePackagesCard;
